import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function StressingSyllableWordsExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.stressingSyllableQuestions)
    ? exercise.stressingSyllableQuestions.map((q) => ({
        ...q,
        type: "input",
        answer: q.correctSyllable,
        skill: "pronunciation",
      }))
    : [];

  /* ================= SAFE EXTERNAL ANSWERS ================= */
  const safeExternalAnswers =
    typeof userAnswer === "object" ? userAnswer : {};

  /* ================= ENGINE ================= */
  const {
    answers,
    setAnswer,
    getInputState,
  } = useExerciseEngine({
    questions,
    showResult: showResults,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC TO PARENT (CRITICAL FIX) ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;
  if (!Object.keys(answers).length) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers]);

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("stressingSyllableQuestions", next);
  };

  const handleChange = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[qIdx] = { ...next[qIdx], [field]: value.trim() };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    updateQuestions([
      ...questions,
      { word: "", correctSyllable: "" },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= EDIT UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <StringField
                  label={`Word ${qIdx + 1}`}
                  value={q.word}
                  onChange={(v) =>
                    handleChange(qIdx, "word", v)
                  }
                  required
                />
              </div>

              {q.word && (
                <SpeakerButton text={q.word} size="lg" />
              )}
            </div>

            <StringField
              label="Stressed Syllable"
              value={q.correctSyllable}
              onChange={(v) =>
                handleChange(qIdx, "correctSyllable", v)
              }
              required
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Word
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Word
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Stressed Syllable</CardTitle>
        <CardDescription>
          Type the stressed syllable of each word
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const userValue = answers[qIdx] || "";

          const inputState = getInputState(
            userValue,
            q.answer
          );

          return (
            <div
              key={qIdx}
              className="p-8 bg-muted rounded-xl space-y-6 text-center"
            >
              {/* WORD */}
              <div className="flex justify-center items-center gap-6">
                <span className="text-4xl font-bold text-blue-700">
                  {q.word}
                </span>
                <SpeakerButton text={q.word} size="xl" />
              </div>

              {/* INPUT */}
              <div className="max-w-xs mx-auto">
                <Input
                  value={userValue}
                  onChange={(e) => {
                    setAnswer(qIdx, e.target.value);
                  }}
                  disabled={showResults}
                  placeholder="Type syllable"
                  className={cn(
                    "text-2xl text-center font-bold h-16 transition border",
                    inputState.className,
                    showResults && "cursor-not-allowed opacity-95"
                  )}
                />
              </div>

              {/* RESULT */}
              {showResults && userValue && (
                <div
                  className={cn(
                    "mt-4 p-4 rounded-lg border text-xl font-semibold",
                    inputState.isCorrect
                      ? "bg-green-100 border-green-400 text-green-700"
                      : "bg-red-100 border-red-400 text-red-700"
                  )}
                >
                  {inputState.isCorrect ? "Correct!" : "Wrong"}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}